/* eslint-disable @typescript-eslint/no-explicit-any */

import React from 'react';
import { convertPeriodToSeconds } from 'helpers/templated-experience.helper';
import useForm from 'hooks/useSimpleForm';
import { FlightNumberInput, FlightSelect, FlightToggleSwitch } from '@flybits/design-system';
import { usePortalContainer } from 'components/Shared/LargeModal/PortalContainerContext';
import { PERIODS, FORM_WIDTHS as WIDTH } from 'components/ExperienceCanvas/constants';
import { PushDraft, PushDraftCallbacks, RuleDraft } from '../pushDraft.types';
import { PUSH_OPTION_DEFAULTS as DEFAULTS } from 'store/push/push.type';

type AdvancedFormProps = Pick<PushDraftCallbacks, 'onChangePushField' | 'onChangeRuleField'> & {
  classPrefix: string;
  pushDraft: PushDraft;
  ruleDraft: RuleDraft;
  deliveryValues: {
    max: number;
    frequency: { value: number; period: { key: string; name: string } };
    limitCheck: string;
    limit: { key: string; name: string };
    remain: string;
    delay: { value: number; period: { key: string; name: string } };
  };
  constraints?: PushDraft['constraints'];
  ruleRefId?: string;
};

enum FieldKeys {
  MAX = 'max',
  FREQ = 'frequency',
  FREQ_P = 'frequencyPeriod',
  LIMIT = 'limit',
  LIMIT_EN = 'limitEnabled',
  REMAIN = 'remain',
  DELAY = 'delay',
  DELAY_P = 'delayPeriod',
}

export default function AdvancedForm({
  classPrefix = '',
  pushDraft,
  ruleDraft,
  deliveryValues,
  constraints,
  ruleRefId,
  onChangePushField,
  onChangeRuleField,
}: AdvancedFormProps) {
  const { formData, updateField } = useForm({
    data: {
      [FieldKeys.MAX]: { value: pushDraft.max ?? deliveryValues.max ?? DEFAULTS.MAX },
      [FieldKeys.FREQ]: { value: deliveryValues.frequency.value },
      [FieldKeys.FREQ_P]: { value: deliveryValues.frequency.period },
      [FieldKeys.LIMIT_EN]: { value: deliveryValues.limitCheck === 'SELECTED' },
      [FieldKeys.LIMIT]: { value: deliveryValues.limit.key },
      [FieldKeys.REMAIN]: {
        value: ruleDraft.emitEveryEvaluationResult ?? deliveryValues.remain === 'SELECTED',
      },
      [FieldKeys.DELAY]: { value: deliveryValues.delay.value },
      [FieldKeys.DELAY_P]: { value: deliveryValues.delay.period },
    },
  });

  const portalContainer = usePortalContainer();

  const handleToggleSwitch = (key: FieldKeys.LIMIT_EN | FieldKeys.REMAIN) => {
    if (key === FieldKeys.LIMIT_EN) {
      if (formData[FieldKeys.LIMIT].value !== '') {
        onChangePushField(
          'limit',
          formData[key].value === true ? 0 : parseInt(formData[FieldKeys.LIMIT].value as string),
        );
      }
    } else {
      onChangeRuleField('emitEveryEvaluationResult', !formData[key].value);
    }
    updateField(key, !formData[key].value);
  };

  const handleSelect = (key: FieldKeys.FREQ_P | FieldKeys.DELAY_P, option: any) => {
    let value: any;
    let stateAttrKey: string;

    switch (key) {
      case FieldKeys.FREQ_P:
        value = convertPeriodToSeconds(formData[FieldKeys.FREQ].value, option.key);
        stateAttrKey = 'cooldown';
        break;
      case FieldKeys.DELAY_P:
        value = convertPeriodToSeconds(formData[FieldKeys.DELAY].value, option.key);
        stateAttrKey = FieldKeys.DELAY;
        break;
    }
    if (value === undefined || stateAttrKey === undefined) return;
    updateField(key, option);
    onChangePushField(stateAttrKey as keyof PushDraft, value);
  };

  const handleInput = (key: FieldKeys.MAX | FieldKeys.LIMIT | FieldKeys.FREQ | FieldKeys.DELAY, value: any) => {
    const val = parseInt(value);
    const isBlank = value === '';
    if (!isBlank && isNaN(val)) return;
    updateField(key, isBlank ? '' : val);
  };

  const handleBlur = (key: FieldKeys.MAX | FieldKeys.LIMIT | FieldKeys.FREQ | FieldKeys.DELAY) => {
    if (formData[key].value === '') return;

    let value: any;
    let stateAttrKey = '';

    switch (key) {
      case FieldKeys.MAX:
      case FieldKeys.LIMIT:
        value = parseInt(formData[key].value);
        stateAttrKey = key;
        break;
      case FieldKeys.FREQ:
        value = convertPeriodToSeconds(formData[key].value, formData[FieldKeys.FREQ_P].value.key);
        stateAttrKey = 'cooldown';
        break;
      case FieldKeys.DELAY:
        value = convertPeriodToSeconds(formData[key].value, formData[FieldKeys.DELAY_P].value.key);
        stateAttrKey = key;
        break;
    }

    if (stateAttrKey === '') return;

    onChangePushField(stateAttrKey as keyof PushDraft, value);
  };

  return (
    <>
      <div className={`${classPrefix}__advanced-title`}> Notification Limit </div>
      <div className={`${classPrefix}__advanced-desc`}>
        Limit the number of times a triggered notification is sent to the same user
      </div>
      <div className={`${classPrefix}__advanced-row`}>
        <span className={`${classPrefix}__advanced-label`}>Send maximum notification(s) of</span>
        <div className={`${classPrefix}__advanced-fields`}>
          <FlightNumberInput
            className={`${classPrefix}__advanced-input`}
            width={WIDTH.SMALL}
            placeholderText={'1'}
            maxValue={999}
            minValue={0}
            onChange={(e: any) => {
              handleInput(FieldKeys.MAX, e.target.value);
            }}
            onBlur={() => handleBlur(FieldKeys.MAX)}
            value={`${formData[FieldKeys.MAX].value}`}
            disabled={constraints?.max?.isReadOnly}
          />
        </div>
      </div>
      <div className={`${classPrefix}__advanced-row`}>
        <span className={`${classPrefix}__advanced-label`}>
          Don&apos;t send more than{' '}
          <span className={`${classPrefix}__advanced-label__highlight`}>{`${formData[FieldKeys.MAX].value}`}</span>{' '}
          notification{formData[FieldKeys.MAX].value > 1 ? 's' : ''} every
        </span>
        <div className={`${classPrefix}__advanced-fields`}>
          <FlightNumberInput
            className={`${classPrefix}__advanced-input`}
            width={WIDTH.SMALL}
            placeholderText={'1'}
            maxValue={999}
            minValue={0}
            onChange={(e: any) => {
              handleInput(FieldKeys.FREQ, e.target.value);
            }}
            onBlur={() => handleBlur(FieldKeys.FREQ)}
            value={formData.frequency.value.toString()}
            disabled={constraints?.cooldown?.isReadOnly}
          />
          <FlightSelect
            label=""
            options={PERIODS.slice(2)}
            hasLabelAnimation={true}
            handleOptionClick={(option: any) => {
              handleSelect(FieldKeys.FREQ_P, option);
            }}
            dropdownMaxHeight={'200px'}
            className={`${classPrefix}__advanced-dropdown`}
            width={WIDTH.MEDIUM}
            dropdownDirection={'bottom'}
            selected={formData.frequencyPeriod.value}
            disabled={constraints?.cooldown?.isReadOnly}
            portalContainer={portalContainer}
          />
        </div>
      </div>
      <div className={`${classPrefix}__advanced-row`}>
        <span className={`${classPrefix}__advanced-label`}> Total maximum notifications per user</span>
        <div className={`${classPrefix}__advanced-fields`}>
          <FlightToggleSwitch
            className={`${classPrefix}__advanced-switch`}
            checked={formData.limitEnabled.value}
            onChange={() => {
              handleToggleSwitch(FieldKeys.LIMIT_EN);
            }}
            disabled={constraints?.limit?.isReadOnly}
          />
          <FlightNumberInput
            className={`${classPrefix}__advanced-input`}
            placeholderText={'1'}
            width={WIDTH.MEDIUM}
            maxValue={999}
            minValue={0}
            disabled={constraints?.limit?.isReadOnly || !formData.limitEnabled.value}
            onChange={(e: any) => {
              handleInput(FieldKeys.LIMIT, e.target.value);
            }}
            onBlur={() => handleBlur(FieldKeys.LIMIT)}
            value={formData.limit.value.toString()}
          />
        </div>
      </div>
      <div className={`${classPrefix}__advanced-row`}>
        <span className={`${classPrefix}__advanced-label`}> Include user who remain in the target audience </span>
        <div className={`${classPrefix}__advanced-fields`}>
          <FlightToggleSwitch
            className={`${classPrefix}__advanced-switch`}
            checked={formData.remain.value}
            disabled={!ruleRefId}
            onChange={() => {
              handleToggleSwitch(FieldKeys.REMAIN);
            }}
          />
        </div>
      </div>
      <div className={`${classPrefix}__advanced-title`}> Notification Delay </div>
      <div className={`${classPrefix}__advanced-desc`}>
        Allows you to delay the notification delivery to your audience
      </div>
      <div className={`${classPrefix}__advanced-row`}>
        <span className={`${classPrefix}__advanced-label`}> Delay each notification by </span>
        <div className={`${classPrefix}__advanced-fields`}>
          <FlightNumberInput
            className={`${classPrefix}__advanced-input`}
            placeholderText={'0'}
            width={WIDTH.SMALL}
            maxValue={999}
            minValue={0}
            onChange={(e: any) => {
              handleInput(FieldKeys.DELAY, e.target.value);
            }}
            onBlur={() => handleBlur(FieldKeys.DELAY)}
            value={formData.delay.value.toString()}
            disabled={constraints?.delay?.isReadOnly}
          />
          <FlightSelect
            label=""
            options={PERIODS.slice(1)}
            hasLabelAnimation={true}
            handleOptionClick={(option: any) => {
              handleSelect(FieldKeys.DELAY_P, option);
            }}
            dropdownMaxHeight={'200px'}
            className={`${classPrefix}__advanced-dropdown`}
            width={WIDTH.MEDIUM}
            dropdownDirection={'bottom'}
            selected={formData.delayPeriod.value}
            disabled={constraints?.delay?.isReadOnly}
            portalContainer={portalContainer}
          />
        </div>
      </div>
    </>
  );
}
